import { useState, useEffect } from 'react'
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Divider,
    CircularProgress
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../config/firebase'
import { Sale, Expense, Partner } from '@starwave/shared/src/types'

export default function FinancePage() {
    const [loading, setLoading] = useState(true)
    const [financials, setFinancials] = useState({
        revenue: 0,
        grossProfit: 0,
        expenses: 0,
        netProfit: 0
    })
    const [partners, setPartners] = useState<Partner[]>([])

    useEffect(() => {
        fetchFinancials()
    }, [])

    const fetchFinancials = async () => {
        try {
            const salesSnap = await getDocs(collection(db, 'sales'))
            const sales = salesSnap.docs.map(doc => doc.data() as Sale)

            const expensesSnap = await getDocs(collection(db, 'expenses'))
            const expenses = expensesSnap.docs.map(doc => doc.data() as Expense)

            const partnersSnap = await getDocs(collection(db, 'partners'))
            const partnersList = partnersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Partner))

            const totalRevenue = sales.reduce((acc, s) => acc + s.totalAmount, 0)
            const totalGrossProfit = sales.reduce((acc, s) => acc + (s.totalGrossProfit || 0), 0)
            const totalExpenses = expenses.reduce((acc, e) => acc + e.amount, 0)
            const netProfit = totalGrossProfit - totalExpenses

            setFinancials({
                revenue: totalRevenue,
                grossProfit: totalGrossProfit,
                expenses: totalExpenses,
                netProfit: netProfit
            })

            setPartners(partnersList)
        } catch (error) {
            console.error('Error fetching financials', error)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Financial Dashboard
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'white', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>Total Revenue</Typography>
                                    <Typography variant="h5" fontWeight="bold">PKR {financials.revenue.toLocaleString()}</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}><AttachMoneyIcon /></Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'white', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>Gross Profit</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="success.main">PKR {financials.grossProfit.toLocaleString()}</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'success.light', color: 'success.main' }}><TrendingUpIcon /></Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'white', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography color="text.secondary" gutterBottom>Expenses</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="error.main">PKR {financials.expenses.toLocaleString()}</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'error.light', color: 'error.main' }}><TrendingDownIcon /></Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: financials.netProfit >= 0 ? 'success.dark' : 'error.dark', color: 'white', height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography color="inherit" sx={{ opacity: 0.9 }} gutterBottom>Net Profit</Typography>
                                    <Typography variant="h5" fontWeight="bold">PKR {financials.netProfit.toLocaleString()}</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}><AccountBalanceWalletIcon /></Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Partner Profit Distribution
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Partner Name</TableCell>
                                <TableCell align="right">Share %</TableCell>
                                <TableCell align="right">Projected Share (PKR)</TableCell>
                                <TableCell align="right">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {partners.map((partner) => {
                                const shareAmount = (financials.netProfit * partner.profitSharePercentage) / 100
                                return (
                                    <TableRow key={partner.id}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{partner.name}</TableCell>
                                        <TableCell align="right">{partner.profitSharePercentage}%</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: shareAmount >= 0 ? 'success.main' : 'error.main' }}>
                                            {shareAmount.toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right">{partner.status}</TableCell>
                                    </TableRow>
                                )
                            })}
                            {partners.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">No partners defined</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    )
}
